/**
 * Run actions of the history (stop a run, cancel/retry/download a long-running
 * operation). Shared by the per-automation history and the row history; each
 * passes the refresh of its own list.
 */
import { workspaceApiFetch } from '@affine/core/modules/cloud';
import { useI18n } from '@affine/i18n';
import { useCallback } from 'react';

import type { LongRunningOpInfo } from '../../services/logging';
import { workflowWorkspaceRoutes } from '../../services/workflow-workspace-routes';

export function historyForegroundPolicy(workspaceId: string, key: string) {
  return {
    workspaceId,
    category: 'foreground-action' as const,
    key: `history:${key}`,
    mode: 'foreground' as const,
    priority: 'foreground' as const,
  };
}

export function useHistoryRunActions(
  workspaceId: string,
  refresh: () => Promise<void>
): {
  cancelOp: (op: LongRunningOpInfo) => Promise<void>;
  cancelExecution: (executionId: string) => Promise<void>;
  retryOp: (op: LongRunningOpInfo) => Promise<void>;
  downloadOp: (op: LongRunningOpInfo) => Promise<void>;
} {
  const t = useI18n();

  // sl-67kc.3.3: Cancel a long-running operation. Returns from the REST
  // call as 202 (cancel queued). The History panel refreshes once Pub/
  // Sub flips the suspension status — but we also fire a delayed refresh
  // here so the user sees the new status without waiting for an
  // unrelated Socket.IO event.
  const issueLroAction = useCallback(
    async (
      url: string,
      failureLabel: string,
      currentJobId: string
    ): Promise<void> => {
      const response = await workspaceApiFetch(
        url,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        },
        historyForegroundPolicy(workspaceId, `lro:${currentJobId}`)
      );
      if (!response.ok) {
        throw new Error(
          `${failureLabel} (jobId=${currentJobId}, status=${response.status})`
        );
      }
      // give the worker a beat then pull fresh state
      await new Promise(r => setTimeout(r, 800));
      await refresh();
    },
    [refresh, workspaceId]
  );

  const handleCancelOp = useCallback(
    async (op: LongRunningOpInfo): Promise<void> => {
      await issueLroAction(
        `/api/stt/jobs/${encodeURIComponent(op.jobId)}/cancel`,
        t['com.streamlain.streamlain.history.lro.cancel-failed'](),
        op.jobId
      );
    },
    [issueLroAction, t]
  );

  const handleCancelExecution = useCallback(
    async (executionId: string): Promise<void> => {
      const response = await workspaceApiFetch(
        workflowWorkspaceRoutes.cancelExecution(workspaceId, executionId),
        { method: 'POST' },
        historyForegroundPolicy(workspaceId, `cancel-execution:${executionId}`)
      );
      if (!response.ok) {
        throw new Error(`Cancel failed (status=${response.status})`);
      }

      const payload = (await response.json()) as { cancelled?: boolean };
      if (payload.cancelled !== true) {
        throw new Error('No running execution was found to cancel.');
      }

      // Pod termination is asynchronous; refresh once the dispatcher has had
      // time to persist the terminal cancelled state.
      await new Promise(r => setTimeout(r, 800));
      await refresh();
    },
    [refresh, workspaceId]
  );

  const handleRetryOp = useCallback(
    async (op: LongRunningOpInfo): Promise<void> => {
      await issueLroAction(
        `/api/stt/jobs/${encodeURIComponent(op.jobId)}/retry-resume`,
        t['com.streamlain.streamlain.history.lro.retry-failed'](),
        op.jobId
      );
    },
    [issueLroAction, t]
  );

  // sl-67kc.3.3 REWORK: Download-Transcript reads the cached provider
  // result buffered on `SuspendedExecution.resumePayload`. We fetch
  // JSON, wrap it in a Blob, and trigger a browser download so the
  // user can inspect or archive the transcript without a copy-paste
  // step. Errors surface on the row's `actionError` state via the
  // thrown rejection (the LongRunningOpRow handle() catch path).
  const handleDownloadOp = useCallback(
    async (op: LongRunningOpInfo): Promise<void> => {
      const response = await workspaceApiFetch(
        `/api/stt/jobs/${encodeURIComponent(op.jobId)}/transcript`,
        { method: 'GET', headers: { Accept: 'application/json' } },
        {
          workspaceId,
          category: 'workspace-api',
          key: `stt-transcript:${op.jobId}`,
          mode: 'background',
          priority: 'visible',
        }
      );
      if (!response.ok) {
        throw new Error(
          `${t['com.streamlain.streamlain.history.lro.download-failed']()} (jobId=${op.jobId}, status=${response.status})`
        );
      }
      const body = await response.blob();
      const href = URL.createObjectURL(body);
      try {
        const anchor = document.createElement('a');
        anchor.href = href;
        anchor.download = `stt-transcript-${op.jobId}.json`;
        document.body.append(anchor);
        anchor.click();
        anchor.remove();
      } finally {
        URL.revokeObjectURL(href);
      }
    },
    [t, workspaceId]
  );

  // Clear all logs via backend API (with confirmation)

  return {
    cancelOp: handleCancelOp,
    cancelExecution: handleCancelExecution,
    retryOp: handleRetryOp,
    downloadOp: handleDownloadOp,
  };
}
